import * as React from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import cn from 'classnames';

import { AviaOrder } from '@websky/graphql';
import { useTheme } from '../../theme';
import { useRetrieveBookingValidation } from '../../RetrieveBooking/hooks';
import Input from '../../Input';
import { BaseButton } from '../../BaseComponents';
import TicketExample from '../../TicketExample';
import BookingPreview from '../../BookingPreview';
import { CdnIcon } from '@shared/ui';

export enum FormType {
	Booking = 'Booking',
	Checkin = 'Checkin'
}

export type findOrderValues = {
	lastName: string;
	ticket: string;
	saveInProfile: boolean;
};

interface Props {
	className?: string;
	type: FormType;
	isLoading?: boolean;
	order?: AviaOrder;
	onClose?: () => void;
	onSubmit?: (values: findOrderValues) => Promise<any>;
	onClearOrder?: () => void;
	onViewOrderClick?: () => void;
}

const AddBooking: React.FC<Props> = ({
	className,
	type,
	isLoading,
	onClose,
	onSubmit,
	order,
	onClearOrder,
	onViewOrderClick
}) => {
	const { t } = useTranslation('Account');
	const { AddBooking: css } = useTheme('AddBooking');

	const isBookingFormType = type === FormType.Booking;

	const {
		lastNameValidation,
		ticketNumberValidation,
		validateByOrderNumber,
		ticketNumberFormat
	} = useRetrieveBookingValidation();

	return (
		<div className={cn(css.wrapper, className)}>
			<div className={css.title}>{type === FormType.Booking ? t('Add booking') : t('Online check-in')}</div>
			<Form
				onSubmit={onSubmit}
				render={({ handleSubmit, values }) => (
					<form onSubmit={handleSubmit}>
						<div className={css.inputs}>
							<Field
								name="lastName"
								validate={(value: string): string | void => {
									if (!value) {
										return t('AddPassenger:Required field') as string;
									}

									if (type === FormType.Checkin) {
										return lastNameValidation(value);
									}
								}}
								render={({ input, meta }) => (
									<Input
										{...input}
										className={css.input}
										inputClassName={css.input_root}
										disabled={!!order || isLoading}
										TextFieldProps={{
											type: 'lastName',
											placeholder: t('Last name'),
											error: (meta.error || meta.submitError) && meta.touched,
											helperText: !!meta.error && meta.touched ? meta.error : '',
											onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
												input.onChange(event);
											}
										}}
									/>
								)}
							/>
							<Field
								name="ticket"
								parse={ticketNumberFormat}
								validate={(value: string): string | void => {
									if (!value) {
										return t('AddPassenger:Required field') as string;
									}
									if (type === FormType.Checkin) {
										return ticketNumberValidation(value);
									}

									if (type === FormType.Booking) {
										if (!ticketNumberValidation(value) || !validateByOrderNumber(value)) {
											return undefined;
										} else {
											return ticketNumberValidation(value) || validateByOrderNumber(value);
										}
									}
								}}
								render={({ input, meta }) => (
									<Input
										{...input}
										className={css.input}
										inputClassName={css.input_root}
										disabled={!!order || isLoading}
										TextFieldProps={{
											type: 'ticket',
											placeholder: t('Ticket or booking number'),
											error: (meta.error || meta.submitError) && meta.touched,
											helperText:
												(meta.error || meta.submitError) && meta.touched
													? meta.submitError ?? meta.error
													: '',
											onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
												input.onChange(event);
											}
										}}
									/>
								)}
							/>
						</div>

						{isBookingFormType && (isLoading || order) ? (
							<BookingPreview
								order={order}
								onClearOrder={onClearOrder}
								isLoading={isLoading}
								redirectViaHistory={false}
								onViewOrderClick={onViewOrderClick}
							/>
						) : (
							<>
								<div className={css.spoilers}>
									<Accordion classes={{ root: css.spoiler, expanded: css.spoilerExpanded }}>
										<AccordionSummary
											classes={{
												root: css.spoilerHeader,
												content: css.spoilerHeaderContent,
												expanded: css.spoilerHeaderExpended
											}}
										>
											<CdnIcon icon="Arrow/Chevron_right" />
											{t('How to find a booking id?')}
										</AccordionSummary>
										<AccordionDetails classes={{ root: css.spoilerDetails }}>
											<p>
												{t(
													'Enter the passengers surname (in latin characters) and ticket number, order ID or booking ID. You can find these in the itinerary receipt sent to purchasers Email after booking.'
												)}
											</p>
											<TicketExample />
										</AccordionDetails>
									</Accordion>
								</div>

								<div className={css.footer}>
									{!isLoading && (
										<BaseButton
											className={cn(css.button, css.buttonCancel)}
											variant="secondary"
											onClick={onClose}
										>
											{t('Cancel')}
										</BaseButton>
									)}
									<BaseButton
										className={cn(css.button, css.buttonContinue, {
											[css.buttonDisabled]: isLoading
										})}
										variant="action"
										type={'submit'}
										disabled={isLoading}
									>
										{t('Continue')}
									</BaseButton>
								</div>
							</>
						)}
					</form>
				)}
			/>
		</div>
	);
};

export default AddBooking;
